import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { NodeType, Pipeline } from '@ui/__generated__/graphql'
import emptyPipelineIllustration from '@ui/assets/illustrations/pipeline_empty.svg'
import { Button } from '@ui/components/core'
import { TableList } from '@ui/components/elements'
import { TableListHeader } from '@ui/components/elements/TableList'
import { MenuItem } from '@ui/components/elements/TableList/TableList'
import { showToast } from '@ui/components/elements/ToastManager'
import useAppNavigation from '@ui/lib/hooks/useAppNavigation'
import { removeTypename } from '@ui/lib/utils'
import { DeletePipelineMutation, GetAllPipelinesQuery, UpdatePipelineMutation } from '@ui/loaders/workflow'
import { useAuth } from '@ui/providers/AuthProvider.tsx'
import { RouteNamesEnum } from '@ui/router/routes'
import { usePipelinesStore } from '@ui/stores'

export const AllPipelines = () => {
  const { navigateTo } = useAppNavigation()
  const { instanceId } = useParams<{ instanceId: string }>()

  const { isDemoMode } = useAuth()

  const { data, loading, error } = GetAllPipelinesQuery()
  const [updatePipeline] = UpdatePipelineMutation()
  const [deletePipeline] = DeletePipelineMutation()

  const [pipelines, setFetchedPipelines] = useState<Pipeline[]>([])

  const { setPipelines } = usePipelinesStore(
    useShallow((state) => ({
      setPipelines: state.setPipelines,
    }))
  )

  useEffect(() => {
    if (data?.allPipelineWorkflows) {
      setFetchedPipelines(data.allPipelineWorkflows)
      setPipelines(data.allPipelineWorkflows)
    }
  }, [data, setPipelines])

  // We only want to get export nodes and their destinations
  const getUniqueDestinations = (pipeline: Pipeline) => {
    return pipeline.nodes
      ?.filter((node) => node?.type === NodeType.Export)
      .map((dest) => dest?.data?.exportType)
      .filter((s): s is string => Boolean(s))
  }

  const pipelineItems = useMemo(() => {
    return pipelines.map((pipeline) => ({
      id: pipeline.id,
      name: pipeline.name,
      status: pipeline.enabled,
      destinations: getUniqueDestinations(pipeline),
    }))
  }, [pipelines])

  const toggleEnablePipeline = useCallback(
    async (pipelineId: string) => {
      const pipeline = pipelines.find((p) => p.id === pipelineId)
      if (!pipeline) return

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { nodes, edges, ...pipelineRest } = pipeline

      try {
        await updatePipeline({
          variables: {
            pipeline: {
              ...removeTypename(pipelineRest),
              enabled: !pipeline.enabled,
            },
          },
        })
      } catch (error) {
        showToast.error(`Failed to ${pipeline.enabled ? 'enable' : 'disable'} pipeline ${pipeline.name}`)
      }
    },
    [pipelines, updatePipeline]
  )

  const handleDeletePipeline = useCallback(
    async (pipelineId: string) => {
      const pipeline = pipelines.find((p) => p.id === pipelineId)
      if (!pipeline) return

      try {
        deletePipeline({
          variables: {
            pipelineId: pipeline.id,
          },
        })

        showToast.success(`Pipeline ${pipeline.name} deleted`)
        setFetchedPipelines(pipelines.filter((p) => p.id !== pipeline.id))
      } catch (error) {
        showToast.error(`Failed to delete pipeline`)
      }
    },
    [deletePipeline, setFetchedPipelines, pipelines]
  )

  const handleSelectPipeline = ({ pipelineId }: { pipelineId: string }) => {
    navigateTo(RouteNamesEnum.EXISTING_PIPELINE, { pipelineId, instanceId })
  }

  const menuItems = useMemo(() => {
    return [
      {
        label: 'Delete',
        onClick: (pipelineId: string) => handleDeletePipeline(pipelineId),
      },
      {
        label: 'Toggle Status',
        onClick: (pipelineId: string) => toggleEnablePipeline(pipelineId),
      },
    ]
  }, [handleDeletePipeline, toggleEnablePipeline])

  if (loading || !data) {
    return <div>Loading...</div>
  }

  if (error) {
    return <div>Error: {error.message}</div>
  }

  return (
    <div className="size-full">
      {pipelineItems.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center space-y-4">
          <img src={emptyPipelineIllustration} alt="No pipelines" className="h-32 w-1/2" />
          <p className="text-center text-lg text-gray-500">No pipelines found</p>
          <Button size="small" onClick={() => navigateTo(RouteNamesEnum.NEW_PIPELINE)}>
            Create your first pipeline
          </Button>
        </div>
      ) : (
        <>
          <div className="size-full space-y-4">
            <TableListHeader
              title="Pipelines"
              length={pipelines.length}
              action={
                isDemoMode ? null : (
                  <Button size="small" onClick={() => navigateTo(RouteNamesEnum.NEW_PIPELINE)}>
                    Add new pipeline
                  </Button>
                )
              }
            />

            <TableList
              items={pipelineItems!}
              logoColumns={['destinations']}
              menuItems={menuItems as MenuItem[]}
              onRowClick={(pipelineId) => handleSelectPipeline({ pipelineId })}
            />
          </div>
        </>
      )}
    </div>
  )
}
